import { useModal, DateField } from '@jutro/components';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { Link } from '@jutro/router';
import GatewayDocumentService from 'ent-capability-gateway-document/services/GatewayDocumentService';
import { AgencyAdminService } from 'twia-capability-agencyadmin';
import { ENTGatewayDocumentService } from 'ent-capability-gateway-document';
import metadata from './TwiaCommissionLanding.metadata.json5';
import messages from './TwiaCommissionLanding.messages';
import styles from './TwiaCommissionLanding.module.scss';

function TwiaCommissionLanding() {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert } = useModal();
    const { authHeader } = useAuthentication();
    const [documentSearch, setDocumentSearch] = useState('');
    const [documents, setDocuments] = useState([]);
    const [displayDocuments, setDisplayDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    let userAgencyDocumentID = '';

    const pageConfig = {
        page: 0,
        pageSize: 1000
    }

    useEffect(() => {
        loadStatements();
    }, []);

    const processResponseData = responseData => {

        if (!_.isEmpty(responseData)) {
            return responseData.map(docInfo => {
                const statements = {
                    name: docInfo.name,
                    dateModified: docInfo.dateModified,
                    docUID: docInfo.docUID,
                    sessionID: docInfo.sessionID,
                    author: docInfo.author
                };

                return statements;
            });
        }

        return [];
    };

    const loadStatements = async () => {
        try {
            setLoading(true);

            let docs;
            const { page, pageSize } = pageConfig;
            const startIndex = pageSize * page;
            const offsetEndMinusOne = startIndex + pageSize - 1;
            const queryOptions = {
                offsetStart: startIndex,
                offsetEnd: offsetEndMinusOne
            }

            const isValidUser = await AgencyAdminService.userHasRole('Financial Admin', authHeader)

            if (isValidUser) {
                const userPreferences = await AgencyAdminService.getCurrentUser(authHeader)

                userAgencyDocumentID = userPreferences?.userAgencyDocumentID_Ext;

                if (userAgencyDocumentID) {
                    const response = await GatewayDocumentService.getCommissionStatements(userAgencyDocumentID, queryOptions, authHeader)

                    docs = processResponseData(response.items);
                    setDocuments(docs);
                    setDisplayDocuments(docs);
                }
            }

            setDocuments(docs);
            setDisplayDocuments(docs);

        } catch (e) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.failedToFetchComission,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            })
        } finally {
            setLoading(false);
        }
    };

    const onChangeDocumentSearch = useCallback(
        searchTerm => {
            if (searchTerm && documents && documents.length > 0) {
                const filtered = documents?.filter(doc => doc.name?.toLowerCase().includes(searchTerm?.toLowerCase()));

                setDisplayDocuments(filtered);
            } else {
                setDisplayDocuments(documents);
            }

            setDocumentSearch(searchTerm);
        },
        [setDocumentSearch, setDisplayDocuments, documents]
    );

    const onDownloadDocument = useCallback(async item => {
        setLoading(true);

        try {
            const downloadURL = ENTGatewayDocumentService.downloadStatement(item.docUID, item.sessionID);

            window.location.href = `${downloadURL}&producer=${userAgencyDocumentID}&author=${item.author}`;
        } catch (error) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.unableToDownloadStatement,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            })
                .catch(_.noop);
        } finally {
            setLoading(false);
        }
    }, [userAgencyDocumentID, showAlert]);

    const getDocumentName = useCallback((item, index) => (
        <Link style={{ justifyItems: 'left' }} onClick={() => { onDownloadDocument(item) }}>{item.name}</Link>
    ), [onDownloadDocument]);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);


    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        documentSearch: {
            visible: documents && documents.length > 0,
            value: documentSearch
        },
        documentsTable: {
            data: displayDocuments,
        },

    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {},
        resolveCallbackMap: {
            onChangeDocumentSearch,
            getDocumentName,
            getDocumentUploaded,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default TwiaCommissionLanding;
